/* ==========================================================================
   chem-compat.js
   Alpine component for the chemical compatibility lookup tool.

   Standalone asset, not part of the Vite-compiled main-*.js bundle — same
   reasoning as chem-compat.css. Re-add after each theme update.

   Registers via Alpine.data(), so it works with x-data="chemCompat()" in
   any markup — the standalone page and the (future) popup both mount this
   same component, just inside different shells. No jQuery, no DOM query
   library — Alpine handles all reactivity directly.
   ========================================================================== */

// Shared, cached fetch: if both the standalone page's component AND a
// popup's component mount on the same page (e.g. global popup trigger
// present in the header on every page, including the standalone page
// itself), this ensures compat-data.json is only ever downloaded once
// per session, not once per mounted component.
window.ChemCompatData = (function () {
  let promise = null;
  return {
    load(url) {
      if (!promise) {
        promise = fetch(url).then((r) => {
          if (!r.ok) throw new Error('compat-data.json request failed: ' + r.status);
          return r.json();
        });
      }
      return promise;
    },
  };
})();

document.addEventListener('alpine:init', () => {
  Alpine.data('chemCompat', (config = {}) => ({
    // ---- config (passed in from the markup, e.g. x-data="chemCompat({...})") ----
    dataUrl: config.dataUrl,
    presetModelCode: config.presetModelCode || null, // for the future contextual popup

    // ---- state ----
    mode: config.presetModelCode ? 'model' : (config.mode || 'liquid'),
    query: '',
    selected: null, // { type: 'liquid'|'model', code: string }
    statusFilter: 'all', // 'all' | 'R' | 'N' | 'U' — mutually exclusive
    focusedIndex: 0, // roving tabindex position within the currently visible listItems
    data: null,
    loading: true,
    error: null,

    // ---- "not available online" modal (quote-only models) ----
    activeModal: null, // the notAvailableModal object of the currently open modal, or null
    modalTrigger: null, // the element that opened the modal, so focus can return to it on close

    async init() {
      this.$watch('query', () => { this.focusedIndex = 0; });
      try {
        this.data = await window.ChemCompatData.load(this.dataUrl);
        if (this.presetModelCode) {
          const preset = this.data.models.find((m) => m.code === this.presetModelCode);
          if (preset) this.selected = { type: 'model', code: preset.code };
        }
      } catch (e) {
        this.error = 'Could not load compatibility data. Please refresh and try again.';
      } finally {
        this.loading = false;
      }
    },

    switchMode(newMode) {
      if (this.mode === newMode) return;
      this.mode = newMode;
      this.query = '';
      this.selected = null;
      this.focusedIndex = 0;
    },

    select(item) {
      this.selected = {
        type: this.mode,
        code: this.mode === 'liquid' ? item.name : item.code,
      };
      // if the currently active status filter has no matching rows for
      // this new selection, fall back to "all" rather than silently
      // showing zero results for a filter that's no longer meaningful
      if (this.statusFilter !== 'all' && !this.presentStatuses[this.statusFilter]) {
        this.statusFilter = 'all';
      }
      // move focus to the results heading: orients keyboard/mobile users
      // to the new view (list is replaced by results on mobile) and gets
      // the selection announced to screen readers via the heading itself
      this.$nextTick(() => {
        if (this.$refs.resultsHeading) this.$refs.resultsHeading.focus();
      });
    },

    selectStatus(code) {
      if (code !== 'all' && !this.presentStatuses[code]) return; // can't select a status with no rows to show
      this.statusFilter = code;
    },

    // Arrow-key navigation for the mode tablist (2 tabs, automatic activation).
    onTabsKeydown(e) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const newMode = this.mode === 'liquid' ? 'model' : 'liquid';
      this.switchMode(newMode);
      this.$nextTick(() => {
        const ref = newMode === 'liquid' ? 'tabFluid' : 'tabModel';
        if (this.$refs[ref]) this.$refs[ref].focus();
      });
    },

    // Arrow-key navigation for the status radiogroup, skipping disabled chips.
    onFilterKeydown(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const order = ['all', 'R', 'N', 'U'];
      const isEnabled = (code) => code === 'all' || this.presentStatuses[code];
      const enabled = order.filter(isEnabled);
      if (!enabled.length) return;
      let pos = enabled.indexOf(this.statusFilter);
      if (pos === -1) pos = 0;
      if (e.key === 'Home') pos = 0;
      else if (e.key === 'End') pos = enabled.length - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') pos = Math.min(pos + 1, enabled.length - 1);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') pos = Math.max(pos - 1, 0);
      const newCode = enabled[pos];
      this.selectStatus(newCode);
      this.$nextTick(() => {
        const btn = this.$el.querySelector('[data-filter-code="' + newCode + '"]');
        if (btn) btn.focus();
      });
    },

    // Arrow-key navigation for the search results listbox (roving tabindex).
    // Without this, every visible item is its own tab stop — with 584
    // liquids that means up to 584 Tab presses just to get past the list.
    onListKeydown(e) {
      const items = this.listItems;
      if (!items.length) return;
      let idx = this.focusedIndex;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          idx = Math.min(idx + 1, items.length - 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          idx = Math.max(idx - 1, 0);
          break;
        case 'Home':
          e.preventDefault();
          idx = 0;
          break;
        case 'End':
          e.preventDefault();
          idx = items.length - 1;
          break;
        default:
          return;
      }
      this.focusedIndex = idx;
      this.$nextTick(() => {
        const buttons = this.$el.querySelectorAll('.chem-compat__list-item');
        if (buttons[idx]) buttons[idx].focus();
      });
    },

    // Opens the "not available online" modal for a quote-only model.
    // triggerEl is captured so focus can return to the exact button that
    // opened it on close, per the same focus-management convention used
    // by resultsHeading/searchInput elsewhere in this component.
    openNotAvailableModal(modal, triggerEl) {
      this.activeModal = modal;
      this.modalTrigger = triggerEl || document.activeElement;
      this.$nextTick(() => {
        if (this.$refs.modalClose) this.$refs.modalClose.focus();
      });
    },

    closeNotAvailableModal() {
      this.activeModal = null;
      this.$nextTick(() => {
        if (this.modalTrigger && this.modalTrigger.focus) this.modalTrigger.focus();
        this.modalTrigger = null;
      });
    },

    // Escape closes; Tab/Shift+Tab cycles focus within the modal (a hand-rolled
    // focus trap, consistent with this file not depending on any extra
    // libraries beyond Alpine).
    onModalKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeNotAvailableModal();
        return;
      }
      if (e.key !== 'Tab' || !this.$refs.modalPanel) return;
      const focusable = this.$refs.modalPanel.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    // modal.message is an optional per-model override (the "escape hatch").
    // Absent that, the copy is picked purely from whether availableSkusUrl
    // exists — no separate flag to keep in sync with it.
    modalMessage(modal) {
      if (!modal) return '';
      if (modal.message) return modal.message;
      return modal.availableSkusUrl
        ? "This exact configuration isn't available as a stocked SKU online, but we do carry other options in this body material. You can also request a quote for this specific configuration."
        : 'This model is available exclusively through our quoting process. Our sales engineers can help you find the right configuration.';
    },

    get presentStatuses() {
      const present = { R: false, N: false, U: false };
      this.allResults.forEach((r) => { present[r.status] = true; });
      return present;
    },

    get formattedRevisionDate() {
      if (!this.data || !this.data.revisionDate) return '';
      const parts = this.data.revisionDate.split('-').map(Number);
      const date = new Date(parts[0], parts[1] - 1, parts[2]); // local, not UTC — avoids off-by-one near midnight
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    },

    get listItems() {
      if (!this.data) return [];
      const q = this.query.trim().toLowerCase();
      if (this.mode === 'liquid') {
        return !q
          ? this.data.liquids
          : this.data.liquids.filter((l) => l.name.toLowerCase().includes(q));
      }
      return !q
        ? this.data.models
        : this.data.models.filter(
            (m) => m.code.toLowerCase().includes(q) || m.description.toLowerCase().includes(q)
          );
    },

    get allResults() {
      if (!this.selected || !this.data) return [];

      if (this.selected.type === 'liquid') {
        const row = this.data.matrix[this.selected.code];
        if (!row) return [];
        return this.data.models
          .map((m, i) => ({
            label: m.code,
            sublabel: m.description,
            status: row[i],
            datasheetUrl: m.datasheetUrl,
            collectionUrl: m.collectionUrl,
            notAvailableModal: m.notAvailableModal,
          }))
          .filter((r) => r.status);
      }

      const modelIdx = this.data.models.findIndex((m) => m.code === this.selected.code);
      const model = modelIdx === -1 ? null : this.data.models[modelIdx];
      if (!model) return [];
      return this.data.liquids
        .map((l) => ({
          label: l.name,
          sublabel: l.classification,
          status: this.data.matrix[l.name] ? this.data.matrix[l.name][modelIdx] : null,
          datasheetUrl: model.datasheetUrl,
          collectionUrl: model.collectionUrl,
          notAvailableModal: model.notAvailableModal,
        }))
        .filter((r) => r.status);
    },

    get results() {
      return this.statusFilter === 'all'
        ? this.allResults
        : this.allResults.filter((r) => r.status === this.statusFilter);
    },

    statusLabel(code) {
      return { R: 'Recommended', N: 'Not recommended', U: 'Unknown' }[code] || '';
    },
  }));
});
